'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type CalculatorControl,
  type SingleColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption, PieSeriesOption } from 'echarts';
import { donutChartDescription } from './donut-chart.description';
import { pieChartConfig, pieChartControls, type PieChartControlValues, getPieChartConfig } from '../pie-chart/pie-chart.config';

// Extend pie chart control values with donut-specific ones
export interface DonutChartControlValues extends Omit<PieChartControlValues, 'centerText'> {
  [ControlIds.InnerRadius]: number;
  [ControlIds.CenterText]?: string;
}

// Add donut-specific controls
export const donutChartControls: { tabs: { title: string; controls: readonly CalculatorControl[]; }[] } = {
  tabs: [
    {
      title: 'Data',
      controls: pieChartControls.tabs[0]?.controls ?? []
    },
    {
      title: 'Appearance',
      controls: [
        ...(pieChartControls.tabs[1]?.controls ?? []),
        {
          id: ControlIds.InnerRadius,
          type: ControlType.Number,
          label: 'Inner radius',
          min: 0,
          max: 90,
          step: 1,
          defaultValue: 50
        } satisfies NumberControl,
        {
          id: ControlIds.CenterText,
          type: ControlType.Text,
          label: 'Center text'
        } satisfies TextControl
      ]
    }
  ]
};

// Extend pie chart config for donut chart
function getDonutChartConfig(pieConfig: EChartsOption, innerRadius: number, centerText?: string): EChartsOption {
  const donutConfig = { ...pieConfig };
  
  // Modify the series configuration for donut chart
  if (donutConfig.series && Array.isArray(donutConfig.series)) {
    donutConfig.series = donutConfig.series.map(series => {
      if (series.type !== 'pie') return series;

      const pieSeries = series as PieSeriesOption;
      const outerRadius = typeof pieSeries.radius === 'string' ? pieSeries.radius : '70%';

      return {
        ...series,
        type: 'pie',
        radius: [`${innerRadius}%`, outerRadius],
        label: pieSeries.label // Keep original label configuration
      };
    });
  }

  // Add center text if provided
  if (centerText) {
    donutConfig.graphic = {
      type: 'text',
      left: 'center',
      top: 'center',
      style: {
        text: centerText,
        fontSize: 20,
        fontWeight: 'bold',
        align: 'center'
      }
    };
  }

  return donutConfig;
}

export const donutChartConfig: CalculatorConfig<CalculatorId.DonutChart> = {
  ...pieChartConfig,
  id: CalculatorId.DonutChart,
  name: 'Donut Chart',
  initialData: TableDataJSON.from({
    columns: [
      { id: 'category', name: 'Category', values: ['Product A', 'Product B', 'Product C', 'Product D'] },
      { id: 'sales', name: 'Sales', values: ['300', '200', '150', '100'] }
    ]
  }),
  initialControlValues: {
    category: 'category',
    value: 'sales',
    title: 'Sales Distribution by Product',
    showLabels: 'true',
    labelPosition: 'outside',
    showPercentages: 'true',
    radius: 75,
    innerRadius: 50,
    centerText: 'Total\n750'
  },
  controls: donutChartControls,
  getVisualizationData: ({ data, controls }) => {
    // Get base pie chart configuration
    const pieConfig = pieChartConfig.getVisualizationData({ data, controls }) as EChartsOption;
    if (!pieConfig) return null;

    // Type assert the controls to our specific type
    const donutControls = controls as DonutChartControlValues;
    
    // Convert to donut chart configuration
    return getDonutChartConfig(
      pieConfig,
      donutControls[ControlIds.InnerRadius],
      donutControls[ControlIds.CenterText]
    );
  }
}; 